import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {isAxiosError} from 'axios'
import {Button} from '@/components/ui/button'
import {
  ConsentForm,
  emptyConsent,
  validateConsent,
  type ConsentErrors,
  type ConsentValues,
} from '@/components/billing/consent-fields'
import {PrecioAcordadoTag} from '@/components/billing/precio-acordado'
import {translateApiError} from '@/lib/errors'
import {formatCopFull} from '@/lib/money'
import {tokenizeWithWidget, WompiWidgetError} from '@/lib/wompi-widget'
import {
  useCreateSubscription,
  type BillingCycle,
  type PlanPrice,
  type SubscriptionActivation,
} from '@/hooks/inci/cartera/use-billing'
import type {BillingState} from '@/hooks/inci/cartera/use-billing'

type Metodo = 'auto' | 'enlace'

/** Solo el cobro automático pide consentimiento: el enlace no guarda ningún medio. */
function validateActivation(metodo: Metodo, consent: ConsentValues): ConsentErrors {
  return metodo === 'auto' ? validateConsent(consent) : {}
}

function CycleToggle({
  value,
  onChange,
  disabled,
}: {
  value: BillingCycle
  onChange: (cycle: BillingCycle) => void
  disabled?: boolean
}) {
  const {t} = useTranslation()
  return (
    <div className="inline-flex rounded-lg border border-border p-0.5" data-testid="billing-cycle-toggle">
      {(['mensual', 'anual'] as const).map((cycle) => (
        <button
          key={cycle}
          type="button"
          disabled={disabled}
          data-testid={`billing-cycle-${cycle}`}
          aria-pressed={value === cycle}
          onClick={() => onChange(cycle)}
          className={
            value === cycle
              ? 'rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground'
              : 'rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-40'
          }>
          {t(`planFacturacion.cycle.${cycle}`)}
        </button>
      ))}
    </div>
  )
}

function PriceCard({
  cycle,
  price,
  selected,
  onSelect,
  disabled,
}: {
  cycle: BillingCycle
  price: PlanPrice | null
  selected: boolean
  onSelect: () => void
  disabled?: boolean
}) {
  const {t} = useTranslation()
  // El IVA sale de cada entrada del price book (`iva_pct`), nunca de un 19 % fijo.
  const iva = price ? Math.round((price.amount_cop * price.iva_pct) / 100) : null
  const total = price && iva != null ? price.amount_cop + iva : null
  return (
    <button
      type="button"
      disabled={disabled || price == null}
      data-testid={`billing-price-card-${cycle}`}
      onClick={onSelect}
      aria-pressed={selected}
      className={
        'flex flex-1 flex-col gap-1.5 rounded-lg border px-4 py-3.5 text-left transition-colors disabled:pointer-events-none disabled:opacity-50 ' +
        (selected ? 'border-primary bg-accent' : 'border-border bg-background hover:border-primary/40')
      }>
      <span className="font-mono text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
        {t(`planFacturacion.cycle.${cycle}`)}
      </span>
      {price == null || total == null || iva == null ? (
        <span className="text-sm text-muted-foreground">{t('planFacturacion.priceUnavailable')}</span>
      ) : (
        <>
          <span className="font-mono text-lg font-semibold text-foreground tabular-nums">{formatCopFull(total)}</span>
          <span className="text-xs text-muted-foreground">
            {t('planFacturacion.priceBreakdown', {base: formatCopFull(price.amount_cop), iva: formatCopFull(iva)})}
          </span>
          <PrecioAcordadoTag price={price} />
        </>
      )}
    </button>
  )
}

/** El cobro automático va marcado por defecto; el enlace sigue visible porque PSE solo existe por ahí. */
function MetodoOption({
  value,
  selected,
  disabled,
  onSelect,
}: {
  value: Metodo
  selected: boolean
  disabled: boolean
  onSelect: () => void
}) {
  const {t} = useTranslation()
  return (
    <label
      className={
        'flex cursor-pointer items-start gap-2.5 rounded-lg border px-4 py-3 transition-colors ' +
        (selected ? 'border-primary bg-accent' : 'border-border hover:border-primary/40')
      }>
      <input
        type="radio"
        name="billing-metodo"
        data-testid={`billing-metodo-${value}`}
        className="mt-1"
        checked={selected}
        disabled={disabled}
        onChange={onSelect}
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground">{t(`planFacturacion.metodo.${value}.label`)}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{t(`planFacturacion.metodo.${value}.hint`)}</span>
      </span>
    </label>
  )
}

/** El cobro automático tokeniza y cobra en la misma llamada (`payment_source`); si la tokenización
 * no termina no se llama al backend y no queda nada a medias. */
export function ActivatePlanBlock({
  businessId,
  state,
  defaultEmail,
  reactivate,
}: {
  businessId: string
  state: BillingState
  defaultEmail: string
  reactivate: boolean
}) {
  const {t} = useTranslation()
  const [cycle, setCycle] = useState<BillingCycle>('mensual')
  const [metodo, setMetodo] = useState<Metodo>('auto')
  const [consent, setConsent] = useState<ConsentValues>({...emptyConsent, email: defaultEmail})
  const [consentErrors, setConsentErrors] = useState<ConsentErrors>({})
  const [gatewayUnconfigured, setGatewayUnconfigured] = useState(false)
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // `wompi`: la ventana segura está abierta y aún no se ha llamado al backend.
  const [enWompi, setEnWompi] = useState(false)
  const create = useCreateSubscription(businessId)

  const gatewayReady = !!state.wompi.public_key
  const efectivo: Metodo = gatewayReady ? metodo : 'enlace'
  const noVigentePrice = (cycle === 'mensual' ? state.price_mensual : state.price_anual) == null
  const pending = create.isPending || enWompi

  function activar(activation: SubscriptionActivation) {
    create.mutate(activation, {
      onSuccess: (data) => {
        // null si la factura nació en cero o se cobró de la fuente tokenizada: no hay checkout.
        setPaymentUrl(data.payment_url)
        if (data.payment_url) window.open(data.payment_url, '_blank', 'noopener')
      },
      onError: (err) => {
        if (isAxiosError(err) && err.response?.status === 503) setGatewayUnconfigured(true)
        else setError(translateApiError(err))
      },
    })
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setGatewayUnconfigured(false)
    setError(null)
    const errors = validateActivation(efectivo, consent)
    setConsentErrors(errors)
    if (Object.values(errors).some(Boolean)) return
    if (efectivo === 'enlace') return activar({billing_cycle: cycle})

    setEnWompi(true)
    try {
      const tokenized = await tokenizeWithWidget(state.wompi)
      // null: cerró la ventana sin tokenizar.
      if (tokenized === null) return
      activar({
        billing_cycle: cycle,
        payment_source: {
          tipo: tokenized.tipo,
          token: tokenized.token,
          acceptance_token: tokenized.acceptanceToken,
          accept_personal_auth: tokenized.acceptPersonalAuth,
          customer_email: consent.email,
          brand: tokenized.brand,
          last4: tokenized.last4,
          exp_month: tokenized.expMonth,
          exp_year: tokenized.expYear,
        },
      })
    } catch (err) {
      setError(err instanceof WompiWidgetError ? t(err.message) : translateApiError(err))
    } finally {
      setEnWompi(false)
    }
  }

  return (
    <div className="surface space-y-3 p-5" data-testid="billing-activate-block">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-heading text-base text-foreground">{t('planFacturacion.activateTitle')}</h2>
        <CycleToggle value={cycle} onChange={setCycle} disabled={pending} />
      </div>
      {reactivate && (
        <p className="text-sm text-muted-foreground" data-testid="billing-reactivate-note">
          {t('planFacturacion.reactivateNote')}
        </p>
      )}
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <PriceCard
          cycle="mensual"
          price={state.price_mensual}
          selected={cycle === 'mensual'}
          onSelect={() => setCycle('mensual')}
          disabled={pending}
        />
        <PriceCard
          cycle="anual"
          price={state.price_anual}
          selected={cycle === 'anual'}
          onSelect={() => setCycle('anual')}
          disabled={pending}
        />
      </div>

      <ConsentForm
        prefix="billing-activate"
        values={consent}
        errors={consentErrors}
        onChange={(patch) => {
          setConsent((c) => ({...c, ...patch}))
          setConsentErrors((ce) => ({...ce, ...Object.fromEntries(Object.keys(patch).map((k) => [k, undefined]))}))
        }}
        onBlurField={(field) => setConsentErrors((ce) => ({...ce, [field]: validateConsent(consent)[field]}))}
        onSubmit={submit}
        showFields={efectivo === 'auto'}
        header={
          <fieldset className="space-y-2" data-testid="billing-metodo">
            <legend className="mb-2 font-mono text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
              {t('planFacturacion.metodo.title')}
            </legend>
            {gatewayReady && (
              <MetodoOption
                value="auto"
                selected={efectivo === 'auto'}
                disabled={pending}
                onSelect={() => setMetodo('auto')}
              />
            )}
            <MetodoOption
              value="enlace"
              selected={efectivo === 'enlace'}
              disabled={pending}
              onSelect={() => setMetodo('enlace')}
            />
          </fieldset>
        }>
        {gatewayUnconfigured && (
          <p className="text-sm text-muted-foreground" data-testid="billing-gateway-unconfigured">
            {t('planFacturacion.gatewayUnconfigured')}
          </p>
        )}
        {error && (
          <p className="text-sm text-destructive" data-testid="billing-activate-error">
            {error}
          </p>
        )}
        {enWompi && (
          <p className="text-sm text-muted-foreground" data-testid="billing-activate-en-wompi">
            {t('planFacturacion.autoDebito.enWompi')}
          </p>
        )}
        {paymentUrl && !gatewayUnconfigured && (
          <p className="text-sm text-muted-foreground" data-testid="billing-payment-pending-note">
            {t('planFacturacion.paymentOpenedNote')}{' '}
            <a href={paymentUrl} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline">
              {t('planFacturacion.paymentLinkAgain')}
            </a>
          </p>
        )}
        <Button
          type="submit"
          data-testid="billing-activate-cta"
          disabled={pending || noVigentePrice || gatewayUnconfigured}>
          {pending
            ? t('common.saving')
            : t(reactivate ? 'planFacturacion.cta.reactivate' : 'planFacturacion.cta.activate')}
        </Button>
      </ConsentForm>
    </div>
  )
}
